import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { NotificationCard } from "@/components/NotificationCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Navbar } from "@/components/Navbar";

type FilterType = "all" | "unread" | "events" | "warning" | "critical" | "breach";

export default function Notifications() {
  const navigate = useNavigate();
  const { notifications, isLoading, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [filter, setFilter] = useState<FilterType>("all");

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === "all") return true;
    if (filter === "unread") return !notification.isRead;
    if (filter === "events") return notification.type === 'event';
    if (filter === "warning") return notification.type === 'tat_breach' && notification.escalation_level === 1;
    if (filter === "critical") return notification.type === 'tat_breach' && notification.escalation_level === 2;
    if (filter === "breach") return notification.type === 'tat_breach' && notification.alert_type === "breach";
    return true;
  });

  const handleViewPatient = (patientId: string) => {
    navigate(`/?patientId=${patientId}`);
  };

  // Counts for tabs
  const eventCount = notifications.filter(n => n.type === 'event').length;
  const warningCount = notifications.filter(n => n.type === 'tat_breach' && n.escalation_level === 1).length;
  const criticalCount = notifications.filter(n => n.type === 'tat_breach' && n.escalation_level === 2).length;
  const breachCount = notifications.filter(n => n.type === 'tat_breach' && n.alert_type === 'breach').length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Bell className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
              className="gap-2"
            >
              <CheckCheck className="h-4 w-4" />
              Mark All Read
            </Button>
          )}
        </div>

        {/* Filters */}
        <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterType)}>
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="all" className="gap-2">
              All
              <Badge variant="secondary" className="rounded-full px-2 py-0">
                {notifications.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="unread" className="gap-2">
              Unread
              {unreadCount > 0 && (
                <Badge variant="secondary" className="rounded-full px-2 py-0">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-2">
              Events
              <Badge variant="secondary" className="rounded-full px-2 py-0">
                {eventCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="warning" className="gap-2">
              Warning
              <Badge variant="secondary" className="rounded-full px-2 py-0">
                {warningCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="critical" className="gap-2">
              Critical
              <Badge variant="secondary" className="rounded-full px-2 py-0">
                {criticalCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="breach" className="gap-2">
              Breach
              <Badge variant="secondary" className="rounded-full px-2 py-0">
                {breachCount}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Notifications List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-4 rounded-full bg-muted/50 mb-4">
              <Bell className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No notifications</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              {filter === "all" 
                ? "You don't have any notifications yet."
                : `No ${filter} notifications to display.`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onViewPatient={handleViewPatient}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
